import {
  useMemo,
  useImperativeHandle,
  forwardRef,
  FC,
  RefAttributes,
  useEffect,
  PropsWithoutRef,
} from 'react';
import {
  ReportContainer,
  ReportContainerProviders,
  ReportStoreSuspense,
} from '@sas-dvr/internal-va-react-core/components/ReportContainer';
import { LoadingText } from '@sas-dvr/internal-va-react-core/components/ReportContainer/LoadingText';
import { ReportContentWrapper } from '@sas-dvr/internal-va-react-core/components/ReportContentWrapper';
import { CoreContextProviders } from '@sas-dvr/internal-va-react-core/components/CoreContextProviders';
import { AuthenticationType } from '@sas-dvr/internal-va-react-core/utils/authorization';
import { useReportContext, useReportStore } from '../internal/hooks';
import { ThemeParameterOverrides } from '@sas/nova-commons/design-tokens';
import { createUniqueId } from '@sas-dvr/internal-va-react-core/components/utils/createUniqueId';
import { ReportHandle } from '../handles';
import { MenuItemProvider } from '@sas-dvr/internal-va-react-core/components/ReportContainer/MenuItemProvider';
import { useRootClassName } from '../config';
import { BirdState } from '@sas-dvr/internal-va-react-core/store/bird/birdState';
import { Provider } from 'react-redux';
import { FeatureFlagOverrideProvider } from './FeatureFlagOverrideProvider';

export type AbstractReportProps = (
  | {
      url: string;
      reportUri: string;
      authenticationType: AuthenticationType;
    }
  | {
      packageUri: string;
    }
) & {
  restrictViewportGestures?: boolean;
  elementKey?: string;
  menuItemProvider?: MenuItemProvider;
  popoverRootId?: string;
} & RefAttributes<ReportHandle>;

export type SASReportProps = {
  hideNavigation?: boolean | 'auto';
  onReportLoad?: () => void;
  themeOverrides?: ThemeParameterOverrides;
  graphCSS?: string;
} & AbstractReportProps;

/**
 * @privateRemarks
 * Typed as FC so the mobile wrappers don't have to be forward ref components. forwardRef also mangles unions of prop types.
 *
 * @internal
 */
export const SASReport: FC<SASReportProps> = forwardRef<ReportHandle, SASReportProps>(
  function SASReport(props: PropsWithoutRef<SASReportProps>, ref) {
    const elementKey = useMemo(() => {
      return props.elementKey === undefined ? createUniqueId() : props.elementKey;
    }, [props.elementKey]);
    const contextProps =
      'url' in props
        ? {
            url: props.url,
            reportUri: props.reportUri,
            authenticationType: props.authenticationType,
          }
        : {
            packageUri: props.packageUri,
          };

    const context = useReportContext({
      ...contextProps,
      contextKey: elementKey,
      graphCSS: props.graphCSS,
    });
    useImperativeHandle(ref, () => context.getReportHandle(), [context]);

    const [store, storeKey] = useReportStore(context, elementKey);
    useEffect(() => {
      store.dispatch(BirdState.actions.setRestrictViewportGestures(props.restrictViewportGestures));
    }, [store, props.restrictViewportGestures]);

    const rootClassName = useRootClassName();
    return (
      <Provider store={store} key={storeKey}>
        <FeatureFlagOverrideProvider>
          <CoreContextProviders popoverRootId={props.popoverRootId}>
            <ReportContentWrapper className={rootClassName}>
              <ReportContainerProviders
                url={contextProps.url}
                packageUri={contextProps.packageUri}
                themeOverrides={props.themeOverrides}
                menuItemProvider={props.menuItemProvider}
                elementType="report"
                authenticationType={contextProps.authenticationType}
              >
                <ReportStoreSuspense
                  url={contextProps.url}
                  authenticationType={contextProps.authenticationType}
                  fallback={() => <LoadingText />}
                >
                  <ReportContainer {...props} />
                </ReportStoreSuspense>
              </ReportContainerProviders>
            </ReportContentWrapper>
          </CoreContextProviders>
        </FeatureFlagOverrideProvider>
      </Provider>
    );
  }
);
